import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { useI18n } from '@/lib/i18n'
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'api/src/types'
import { ChevronRight, Loader2, RefreshCw } from 'lucide-react'
import { useCallback, useEffect } from 'react'
import type { MouseEvent, ReactNode } from 'react'

type Outputs = inferRouterOutputs<AppRouter>['tournamentRouter']
type Inputs = inferRouterInputs<AppRouter>['tournamentRouter']

export type Edition = Outputs['adminListEditions']['editions'][number]
export type Participant = Outputs['adminListParticipants']['participants'][number]
export type ParticipantDetail = Outputs['adminGetParticipant']
export type RecordEntry = ParticipantDetail['records'][number]
export type PhaseFilter = NonNullable<Inputs['adminListEditions']['phase']>
export type StatusFilter = NonNullable<Inputs['adminListParticipants']['status']>
export type Navigation = {
  editionId: string | null
  participantId: string | null
  editionPage: number
  phase: PhaseFilter
  participantPage: number
  search: string
  status: StatusFilter
}
export type Navigate = (patch: Partial<Navigation>, replace?: boolean, scroll?: boolean) => void
export type Copy = (key: string, values?: Record<string, string | number>) => string

export const phaseKeys = {
  ALL: 'allPhases',
  NOT_STARTED: 'forthcoming',
  UNSCHEDULED: 'unscheduled',
  UPCOMING_REGISTRATION: 'upcomingRegistration',
  REGISTRATION_OPEN: 'registrationOpen',
  WAITING_FOR_COMPETITION: 'waitingForCompetition',
  COMPETITION_OPEN: 'running',
  ENDED: 'ended',
} satisfies Record<PhaseFilter, string>

export const statusKeys = {
  ALL: 'allStatuses',
  REGISTERED: 'registered',
  NOT_STARTED: 'notPlaying',
  IN_PROGRESS: 'active',
  READY: 'ready',
  COMPLETED: 'complete',
  INCOMPLETE: 'incomplete',
} satisfies Record<StatusFilter, string>

export const phases = Object.keys(phaseKeys) as PhaseFilter[]
export const statuses = Object.keys(statusKeys) as StatusFilter[]

export function useTournamentCopy() {
  const { t, language } = useI18n()
  const copy = useCallback<Copy>((key, values = {}) => {
    return t(`admin.tournament.${key}`).replaceAll(/\{(\w+)\}/g, (match, name: string) =>
      String(values[name] ?? match))
  }, [t])
  return { copy, t, language: language === 'zh' ? 'zh' as const : 'en' as const }
}

export function utcDate(value: string | null | undefined, compact = false) {
  if (!value)
    return '—'
  const iso = new Date(value).toISOString()
  return compact ? iso.slice(5, 16).replace('T', ' ') : iso.slice(0, 16).replace('T', ' ')
}

export function roleLabel(role: string, copy: Copy) {
  if (role === 'mechanical_wolf')
    return copy('wolf')
  return ['psychic', 'guard', 'hunter', 'witch'].includes(role) ? copy(role) : role
}

export function categoryLabel(category: { id: string, allowedRoles: string[] }, copy: Copy) {
  if (category.id === 'god')
    return copy('god')
  if (category.id === 'mechanical_wolf')
    return copy('wolf')
  return category.allowedRoles.map(role => roleLabel(role, copy)).join(' · ') || category.id
}

export function quotaSummary(edition: Pick<Edition, 'categories' | 'total'>, copy: Copy) {
  const god = edition.categories.find(category => category.id === 'god')
  const wolf = edition.categories.find(category => category.id === 'mechanical_wolf')
  return edition.categories.length === 2 && god && wolf
    ? copy('progressDescription', { godLimit: god.limit, wolfLimit: wolf.limit, totalLimit: edition.total })
    : edition.categories.map(category => `${categoryLabel(category, copy)} ${category.limit}`).join(' · ')
}

export function navigationUrl(navigation: Navigation, patch: Partial<Navigation> = {}) {
  const next = { ...navigation, ...patch }
  const url = new URL(window.location.href)
  const values: Record<string, string | null> = {
    tournament: next.editionId,
    participant: next.editionId ? next.participantId : null,
    tournamentPage: next.editionPage > 1 ? String(next.editionPage) : null,
    tournamentPhase: next.phase !== 'ALL' ? next.phase : null,
    participantPage: next.editionId && next.participantPage > 1 ? String(next.participantPage) : null,
    tournamentSearch: next.editionId && next.search ? next.search : null,
    participantStatus: next.editionId && next.status !== 'ALL' ? next.status : null,
  }
  for (const [key, value] of Object.entries(values)) {
    if (value === null)
      url.searchParams.delete(key)
    else
      url.searchParams.set(key, value)
  }
  url.hash = 'tournaments'
  return `${url.pathname}${url.search}${url.hash}`
}

export function InternalLink({ navigation, patch, navigate, children, className }: {
  navigation: Navigation
  patch: Partial<Navigation>
  navigate: Navigate
  children: ReactNode
  className?: string
}) {
  const click = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return
    event.preventDefault()
    navigate(patch)
  }
  return <a href={navigationUrl(navigation, patch)} onClick={click} className={className}>{children}</a>
}

export function useBoundaryRefresh(
  lifecycle: { serverNow: string, nextTransitionAt: string | null } | undefined,
  updatedAt: number,
  refetch: () => Promise<unknown>,
) {
  const serverNow = lifecycle?.serverNow
  const nextTransitionAt = lifecycle?.nextTransitionAt
  useEffect(() => {
    if (!nextTransitionAt || !serverNow)
      return
    const remaining = Date.parse(nextTransitionAt) - Date.parse(serverNow)
      - Math.max(0, Date.now() - updatedAt)
    const timer = window.setTimeout(() => { void refetch() }, Math.min(Math.max(100, remaining + 150), 2_147_483_647))
    return () => window.clearTimeout(timer)
  }, [serverNow, nextTransitionAt, updatedAt, refetch])
}

export function StatusBadge({ label, tone = 'neutral' }: {
  label: string
  tone?: 'neutral' | 'positive' | 'active' | 'negative'
}) {
  const tones = {
    neutral: 'border-border bg-muted text-foreground',
    positive: 'border-transparent bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-300',
    active: 'border-transparent bg-blue-50 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
    negative: 'border-transparent bg-destructive/10 text-destructive dark:text-red-300',
  }
  return <span className={`inline-flex max-w-full rounded-md border px-2 py-1 text-xs font-medium ${tones[tone]}`}>{label}</span>
}

export function PhaseBadge({ edition, copy }: { edition: Pick<Edition, 'lifecycle'>, copy: Copy }) {
  const { phase } = edition.lifecycle
  return <StatusBadge label={copy(phaseKeys[phase])} tone={phase === 'REGISTRATION_OPEN' || phase === 'COMPETITION_OPEN' ? 'active' : 'neutral'} />
}

export function ParticipantStatus({ status, copy }: { status: Exclude<StatusFilter, 'ALL'>, copy: Copy }) {
  return <StatusBadge label={copy(statusKeys[status])} tone={status === 'IN_PROGRESS' ? 'active' : status === 'COMPLETED' ? 'positive' : 'neutral'} />
}

export function EmailStatus({ status, copy }: { status: Participant['emailStatus'], copy: Copy }) {
  const key = { EMAIL_VERIFIED: 'verified', EMAIL_UNVERIFIED: 'unverifiedEmail', EMAIL_UNBOUND: 'unboundEmail', VISITOR: 'accountIneligible' }[status]
  return <StatusBadge label={copy(key)} tone={status === 'EMAIL_VERIFIED' ? 'positive' : 'neutral'} />
}

export function Metric({ label, value, detail }: { label: string, value: ReactNode, detail?: ReactNode }) {
  return (
    <div className="min-w-0 rounded-lg border border-border p-4">
      <div className="text-xs text-muted-foreground sm:text-sm">{label}</div>
      <div className="mt-2 text-2xl font-semibold tabular-nums tracking-tight">{value}</div>
      {detail && <div className="mt-1 text-xs text-muted-foreground">{detail}</div>}
    </div>
  )
}

export function ProgressBar({ completed, total, label }: { completed: number, total: number, label: string }) {
  const percent = total > 0 ? Math.min(100, Math.max(0, completed / total * 100)) : 0
  return (
    <div className="min-w-0">
      <div className="font-semibold tabular-nums">{completed}<span className="font-normal text-muted-foreground"> / {total}</span></div>
      <div role="progressbar" aria-label={label} aria-valuenow={completed} aria-valuemin={0} aria-valuemax={total} className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-foreground" style={{ width: `${percent}%` }} />
      </div>
    </div>
  )
}

export function QueryNotice({ pending, error, hasData, notFound = false, onRetry }: {
  pending: boolean
  error: boolean
  hasData: boolean
  notFound?: boolean
  onRetry: () => void
}) {
  const { copy, t } = useTournamentCopy()
  if (pending && !hasData)
    return <div role="status" className="flex items-center gap-2 rounded-lg border border-border p-6 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" aria-hidden="true" />{t('admin.statics.loadingData')}</div>
  if (!error)
    return null
  return (
    <div role="alert" className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted p-4 text-sm">
      <span>{copy(notFound ? 'recordNotFound' : hasData ? 'dataStale' : 'dataUnavailable')}</span>
      <Button variant="outline" size="sm" onClick={onRetry}>{copy('retry')}</Button>
    </div>
  )
}

export function RefreshButton({ fetching, onRefresh }: { fetching: boolean, onRefresh: () => void }) {
  const { t } = useTournamentCopy()
  return <Button type="button" variant="outline" onClick={onRefresh} disabled={fetching}>{fetching ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <RefreshCw className="size-4" aria-hidden="true" />}{t(fetching ? 'admin.statics.refreshing' : 'admin.statics.refresh')}</Button>
}

export function Breadcrumbs({ navigation, navigate, edition }: { navigation: Navigation, navigate: Navigate, edition?: Pick<Edition, 'name'> }) {
  const { copy } = useTournamentCopy()
  return (
    <nav aria-label={copy('nav')} className="mb-5 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
      <InternalLink navigation={navigation} patch={{ editionId: null, participantId: null }} navigate={navigate} className="hover:underline">{copy('nav')}</InternalLink>
      {edition && <><ChevronRight className="size-4" aria-hidden="true" /><InternalLink navigation={navigation} patch={{ participantId: null }} navigate={navigate} className={navigation.participantId ? 'hover:underline' : 'text-foreground'}>{edition.name}</InternalLink></>}
      {navigation.participantId && <><ChevronRight className="size-4" aria-hidden="true" /><span className="text-foreground" aria-current="page">{copy('participantView')}</span></>}
    </nav>
  )
}

export function EditionSchedule({ edition }: { edition: Omit<Edition, 'stats'> }) {
  const { copy } = useTournamentCopy()
  return (
    <Card className="gap-0 overflow-hidden">
      <CardHeader className="pb-5"><div className="flex flex-wrap items-center justify-between gap-3"><div><h3 className="text-lg font-semibold">{copy(edition.lifecycle.competitionOpen || edition.lifecycle.ended ? 'progress' : 'registrationInfo')}</h3><p className="mt-1 text-sm text-muted-foreground">{quotaSummary(edition, copy)}</p></div><PhaseBadge edition={edition} copy={copy} /></div></CardHeader>
      <CardContent className="grid gap-5 border-t border-border bg-muted/50 px-6 py-5 sm:grid-cols-3">
        <div><div className="mb-2 text-xs text-muted-foreground">{copy('registration')}</div><div className="text-sm tabular-nums">{edition.datesPlaceholder ? copy('datesPending') : <>{utcDate(edition.registrationStartsAt)}<br />{utcDate(edition.registrationEndsAt)}</>}</div></div>
        <div><div className="mb-2 text-xs text-muted-foreground">{copy('competition')}</div><div className="text-sm tabular-nums">{edition.datesPlaceholder ? copy('datesPending') : <>{utcDate(edition.competitionStartsAt)}<br />{utcDate(edition.competitionEndsAt)}</>}</div></div>
        <div><div className="mb-2 text-xs text-muted-foreground">{copy('quota')}</div><div className="text-sm font-medium">{quotaSummary(edition, copy)}</div><p className="mt-2 text-xs text-muted-foreground">{copy('dateZone')}</p>{!edition.enabled && <div className="mt-2"><StatusBadge label={copy('disabled')} /></div>}{edition.lifecycle.ended && <div className="mt-2"><StatusBadge label={copy(edition.resultsPublishedAt ? 'resultsPublished' : 'resultsPending')} /></div>}{edition.lifecycle.phase === 'WAITING_FOR_COMPETITION' && <p className="mt-2 text-xs text-muted-foreground">{copy('registrationClosed')}</p>}</div>
      </CardContent>
    </Card>
  )
}

export function Pagination({ paging, fetching, onPage }: { paging: { page: number, pageSize: number, total: number, pageCount: number }, fetching: boolean, onPage: (page: number) => void }) {
  const { copy, t } = useTournamentCopy()
  const from = paging.total ? (paging.page - 1) * paging.pageSize + 1 : 0
  const to = Math.min(paging.page * paging.pageSize, paging.total)
  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
      <span>{copy('pageRange', { from, to, total: paging.total })}</span>
      <div className="flex items-center justify-between gap-3"><Button variant="outline" size="sm" disabled={fetching || paging.page <= 1} onClick={() => onPage(paging.page - 1)}>{t('admin.query.previous')}</Button><span className="tabular-nums">{paging.page} / {Math.max(1, paging.pageCount)}</span><Button variant="outline" size="sm" disabled={fetching || paging.page >= paging.pageCount} onClick={() => onPage(paging.page + 1)}>{t('admin.query.next')}</Button></div>
    </div>
  )
}
